import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { bool, node, oneOf, string } from 'prop-types';
import { CollapsePanelProps, CollapsePanelVariants } from './collapse-panel-types';
import styles from './collapse-panel.module.scss';
import { debounce } from '../../../../utils/debounce';
import { canUseDOM } from '../../../../utils/can-use-dom';

const CollapsePanel = ({
  isOpen,
  children,
  previewContent,
  trigger,
  variant = CollapsePanelVariants.Default,
  addClass,
}: CollapsePanelProps): React.ReactElement => {
  const collapseRef = useRef<HTMLDivElement>(null);

  const [collapseState, setCollapseState] = useState(!isOpen);

  const setCollapseHeight = () => {
    const collapseHeight = variant === CollapsePanelVariants.FadePreview ? 250 : 0;
    /* istanbul ignore next */
    if (collapseRef && collapseRef.current) {
      collapseRef.current.style.maxHeight = collapseState
        ? `${collapseHeight}px`
        : `${collapseRef.current.scrollHeight + collapseRef.current.offsetTop}px`;
    }
  };

  const debouncedHandleResize = debounce(setCollapseHeight, 250); // 250ms wait time to fire resize

  useEffect(() => {
    // sets collapse div height when collapseState updates, and resets listener
    setCollapseHeight();
    /* istanbul ignore next */
    if (canUseDOM()) {
      window.addEventListener('resize', debouncedHandleResize);
    }

    return () => {
      /* istanbul ignore next */
      if (canUseDOM()) {
        window.removeEventListener('resize', debouncedHandleResize);
      }
    };
  }, [collapseState, children]);

  useEffect(() => {
    // Control collapse state using the isOpen prop
    setCollapseState(!isOpen);
    // Matching timing function from css to control the overflow visible property
    if (isOpen && collapseRef && collapseRef.current) {
      setTimeout(() => {
        /* istanbul ignore next */
        if (collapseRef && collapseRef.current) {
          collapseRef.current.classList.remove(styles.expanding);
        }
      }, 240);
    }
  }, [isOpen]);

  const classes = classNames(styles['collapse-container'], styles[`${variant}`], !isOpen && styles.collapsed, addClass);

  const contentClasses = classNames(styles['collapse-content'], isOpen && styles.expanding);

  const collapsePreviewSection = previewContent && <div className={styles['collapse-preview']}>{previewContent}</div>;

  return (
    <div className={classes}>
      {trigger}
      {collapsePreviewSection}
      <div className={contentClasses} ref={collapseRef}>
        {children}
      </div>
    </div>
  );
};

CollapsePanel.propTypes = {
  /** Whether or not the panel is expanded */
  isOpen: bool.isRequired,
  /** Content to display within the panel */
  children: node.isRequired,
  /** Preview content is displayed before the panel is expanded. Preview Content should only be used with the FadePreview CollapsePanelVariant */
  previewContent: node,
  /** Element which expands or collapses the panel on click */
  trigger: node,
  /** Preset styling and behavior. Options are CollapsePanelVariants.FadePreview and CollapsePanelVariants.Default */
  variant: oneOf([CollapsePanelVariants.FadePreview, CollapsePanelVariants.Default]).isRequired,
  /** Class to append to CollapsePanel component */
  addClass: string,
};

export { CollapsePanel };
